import { Link } from "@tanstack/react-router"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  useIsAdmin,
  useServerIsAdmin,
  useAdminLocal,
  setAdminLocal,
} from "@/hooks/use-is-admin"
import { Switch } from "@/components/ui/switch"
import { cn } from "@/lib/utils"
import { layoutContainerClassName } from "./constants"
import { LogoIcon } from "./logo"
import { ADMIN_NAV_ITEM } from "./admin-controls"
import {
  RiUserLine,
  RiSettings3Line,
  RiLogoutBoxLine,
  RiShieldLine,
  RiBugLine,
} from "@remixicon/react"

interface HeaderProps {
  displayName: string
  onSignOut: () => void
}

/** Desktop-only header; the phone layout uses the tab bar instead. */
export const Header = ({ displayName, onSignOut }: HeaderProps) => {
  const isAdmin = useIsAdmin()
  const serverIsAdmin = useServerIsAdmin()
  const adminLocal = useAdminLocal()

  return (
    <header className="bg-background">
      <div className={cn(layoutContainerClassName)}>
        <div className="flex h-16 items-center gap-6 border-b">
          <LogoIcon />

          <div className="ml-auto flex items-center gap-3">
            {displayName && (
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <button className="inline-flex items-center gap-2 rounded-4xl border border-border bg-background px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground" />
                  }
                >
                  <span
                    aria-hidden
                    className="inline-block size-[10px] rounded-full bg-primary"
                  />
                  {displayName}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {serverIsAdmin && (
                    <>
                      <DropdownMenuItem
                        closeOnClick={false}
                        className="justify-between"
                      >
                        <span className="flex items-center gap-2.5">
                          <RiBugLine />
                          Admin mode
                        </span>
                        <Switch
                          id="admin-override-desktop"
                          checked={adminLocal}
                          onCheckedChange={setAdminLocal}
                        />
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {isAdmin && (
                    <>
                      <DropdownMenuItem
                        render={<Link to={ADMIN_NAV_ITEM.to} />}
                      >
                        <RiShieldLine />
                        {ADMIN_NAV_ITEM.label}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem render={<Link to="/account" />}>
                    <RiUserLine />
                    Account
                  </DropdownMenuItem>
                  <DropdownMenuItem render={<Link to="/settings" />}>
                    <RiSettings3Line />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem variant="destructive" onClick={onSignOut}>
                    <RiLogoutBoxLine />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
